import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { post } from '../lib/api';
import { setAppBadge } from '../lib/push';
import { ErrorNote, Spinner } from '../components/ui';
import Celebration, { type CelebrationKind } from '../components/Celebration';
import ClaimSheet from '../components/ClaimSheet';

const PetCanvas = lazy(() => import('../pet/PetCanvas'));
import { useBoard } from './KidHome';
import { formatDate, formatMinutes, money, relativeDue } from '../lib/format';
import type { Board, Claim, Extra, Occurrence, QuestWords, TickResult, TurnJob } from '../lib/types';

const TICK_DELAY_MS = 460; // let the tick animation play before the row moves to "Done"

const TICK = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

function paydayWord(board: Board): string {
  if (board.payday.date === board.date) return 'today';
  if (board.payday.periodType === 'month') return 'the end of the month';
  return formatDate(board.payday.date).split(' ')[0]; // "Sun"
}

function paydayPossessive(board: Board): string {
  if (board.payday.date === board.date) return "today's";
  if (board.payday.periodType === 'month') return "this month's";
  const day = new Date(`${board.payday.date}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: 'long',
    timeZone: 'UTC',
  });
  return `${day}'s`;
}

/** Points are "coins" to the kids. */
function coins(n: number): string {
  return `🪙 ${n}`;
}

export default function QuestHome(): JSX.Element {
  const { board, error, toggle, snooze, reload } = useBoard();
  const [popping, setPopping] = useState<string | null>(null);
  const [claiming, setClaiming] = useState<Extra | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [queue, setQueue] = useState<{ kind: CelebrationKind; detail?: string | number }[]>([]);
  const previous = useRef<Board | null>(null);
  const announced = useRef(false);

  // Celebrate transitions, never states: finishing the day, or crossing the payday line.
  useEffect(() => {
    if (!board) return;
    const before = previous.current;
    previous.current = board;
    if (!before) return;
    if (!before.payday.qualifies && board.payday.qualifies) setQueue((q) => [...q, { kind: 'payday' }]);
    else if (!before.streak.todayComplete && board.streak.todayComplete && board.streak.todayHasChores) {
      setQueue((q) => [...q, { kind: 'day' }]);
    }
  }, [board]);

  // News from while they were away: a shield spent, or a milestone banked.
  useEffect(() => {
    if (!board || announced.current) return;
    const news: { kind: CelebrationKind; detail?: string | number }[] = [];
    for (const save of board.streak.unseenSaves) {
      news.push({ kind: save.kind === 'shield' ? 'shield' : save.kind === 'parent' ? 'restored' : 'repair', detail: save.date });
    }
    if (news.length === 0) return;
    announced.current = true;
    setQueue((q) => [...news, ...q]);
    void post('/api/celebrations/seen').catch(() => undefined);
  }, [board]);

  const closeCelebration = useCallback(() => setQueue((q) => q.slice(1)), []);
  const celebration = queue[0] ?? null;

  // Outstanding chores show as a count on the app icon.
  useEffect(() => {
    if (!board) return;
    setAppBadge(board.overdue.length + board.dueToday.length);
  }, [board]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  async function claimed(claim: Claim) {
    setClaiming(null);
    setToast(
      claim.status === 'approved'
        ? `⭐ +${money(claim.amount, board?.payday.currency ?? '')} added!`
        : `⭐ Sent to Mum & Dad: ${money(claim.amount, board?.payday.currency ?? '')}`,
    );
    await reload();
  }

  async function tap(occ: Occurrence) {
    const key = `${occ.choreId}:${occ.periodKey}`;
    if (occ.state === 'done') {
      await toggle(occ, false);
      return;
    }
    if (popping) return;
    setPopping(key);
    await new Promise((resolve) => setTimeout(resolve, TICK_DELAY_MS));
    setPopping(null);
    const result = (await toggle(occ, true)) as TickResult | null;
    if (!result) return;
    const news: { kind: CelebrationKind; detail?: string | number }[] = [];
    if (result.repaired) news.push({ kind: 'repair' });
    for (const milestone of result.badges) news.push({ kind: 'milestone', detail: milestone });
    if (news.length > 0) setQueue((q) => [...q, ...news]);
    else if (result.shieldEarned) setToast('🛡️ Shield earned — one bad day covered');
    else if (result.selfStart) setToast('⚡ Self-start bonus! +1 🪙');
  }

  /** Turn jobs have no due date, so taking one is a plain log, not a tick. */
  async function takeTurn(job: TurnJob) {
    try {
      await post(`/api/turn-jobs/${job.id}/turns`);
      setToast(`${job.icon} Your turn logged · +${job.points} 🪙`);
      await reload();
    } catch (err) {
      setToast((err as Error).message);
    }
  }

  if (error && !board) return <ErrorNote error={error} />;
  if (!board) return <Spinner />;

  const { streak, payday, child } = board;
  // Everything still to come inside this pay period, so the list adds up to
  // the bar: the reminder window first, then the rest of the week.
  const ahead = [...board.soon, ...board.restOfPeriod];
  const period = payday.periodType === 'month' ? board.month : board.week;
  const needed = Math.max(1, Math.ceil(period.threshold * period.totalPoints - 1e-9));
  const progress = period.totalPoints === 0 ? 0 : Math.min(1, period.earnedPoints / period.totalPoints);
  const marker = period.totalPoints === 0 ? 1 : needed / period.totalPoints;
  const nothingLeft = board.overdue.length + board.dueToday.length === 0;

  const streakLabel = streak.days > 0
    ? `🔥 ${streak.days} day streak${streak.todayComplete || !streak.todayHasChores ? '' : ' · finish today to keep it'}`
    : streak.todayHasChores
      ? '✨ Finish today to start a streak'
      : '';
  const shieldLabel = streak.shields > 0 ? `🛡️ ${streak.shields}` : null;

  const paydayToday = payday.date === board.date;
  const hint = period.allowance === 0
    ? 'No pocket money set up yet — ask Mum or Dad.'
    : payday.qualifies
      ? paydayToday
        ? 'Payday is today — you made it! 🎉'
        : `Payday unlocked! Keep it up until ${paydayWord(board)}.`
      : `${payday.pointsToGo} more coin${payday.pointsToGo === 1 ? '' : 's'} and ${paydayPossessive(board)} money is yours.`;

  return (
    <>
      <ErrorNote error={error} />
      <div className="q-hello">Hej {child.name}</div>
      <div className="row wrap" style={{ gap: 8 }}>
        {streakLabel && <span className={`q-streak${streak.days > 0 ? '' : ' cold'}`}>{streakLabel}</span>}
        {shieldLabel && (
          <span className="q-streak" title="A shield covers one bad day">
            {shieldLabel}
          </span>
        )}
      </div>

      {streak.repair && (
        <div className="q-repair">
          <div className="q-repair-title">🩹 Get your {streak.repair.streakAtStake}-day streak back</div>
          <div className="q-repair-body">
            You missed {streak.repair.missing.map((m) => m.title).join(', ')} yesterday. Finish{' '}
            {streak.repair.missing.length === 1 ? 'it' : 'them'} today and the streak counts again — after that it&#39;s gone.
          </div>
        </div>
      )}

      <div className={`q-goal${payday.qualifies ? ' unlocked' : ''}`}>
        <div className="q-goal-row">
          <span>{coins(period.earnedPoints)} of {period.totalPoints} this {payday.periodType}</span>
          <span>{money(period.allowance, period.currency)}</span>
        </div>
        <div className="q-track">
          <span style={{ width: `${progress * 100}%` }} />
          <i style={{ left: `${marker * 100}%` }} />
        </div>
        <div className="q-goal-hint">{hint}</div>
        {board.bonusCoins > 0 && (
          <div className="q-goal-hint" style={{ marginTop: 4, color: 'var(--coin)' }}>
            ⚡ {board.bonusCoins} self-start bonus {board.bonusCoins === 1 ? 'coin' : 'coins'} — you beat the reminder
          </div>
        )}
        {(period.extrasApproved > 0 || period.extrasPending > 0) && (
          <div className="q-goal-hint" style={{ marginTop: 4, color: 'var(--coin)' }}>
            ⭐ {period.extrasApproved > 0 && `+${money(period.extrasApproved, period.currency)} extra`}
            {period.extrasApproved > 0 && period.extrasPending > 0 && ' · '}
            {period.extrasPending > 0 && `${money(period.extrasPending, period.currency)} waiting for a parent`}
          </div>
        )}
      </div>

      {board.pet ? (
        <Link to="/pet" className="pet-card">
          <Suspense fallback={<div className="pet-canvas" style={{ height: 104 }} />}>
            <PetCanvas
              species={board.pet.species}
              stage={board.pet.stage}
              equipped={board.pet.equipped}
              height={104}
              interactive={false}
            />
          </Suspense>
          <span className="grow">
            <span className="pet-level">Level {board.pet.level}</span>
            <span className="pet-name" style={{ display: 'block' }}>{board.pet.name}</span>
            <span className="q-track" style={{ height: 10, marginTop: 6 }}>
              <span
                style={{
                  width: `${
                    board.pet.xpForNextLevel === null
                      ? 100
                      : Math.min(100, (board.pet.xpIntoLevel / board.pet.xpForNextLevel) * 100)
                  }%`,
                }}
              />
            </span>
            {!board.pet.playedToday && <span className="pet-play-pill">Play · +1 XP</span>}
          </span>
        </Link>
      ) : (
        <Link to="/pet" className="pet-card" style={{ padding: '16px 18px' }}>
          <span className="grow">
            <span className="pet-name">Choose your pet 🐾</span>
            <span className="q-goal-hint" style={{ display: 'block' }}>
              It grows every time you do your chores.
            </span>
          </span>
        </Link>
      )}

      {board.house && (
        <div className="q-house">
          <span className="q-house-icon">🏠</span>
          <span className="grow">
            <b>House streak: {board.house.days} {board.house.days === 1 ? 'day' : 'days'}</b>
            <span className="q-goal-hint" style={{ display: 'block' }}>
              {board.house.waitingOn.length === 0
                ? board.house.toReward === 1
                  ? `One more day for ${board.house.reward}!`
                  : `${board.house.toReward} more days for ${board.house.reward}.`
                : `Waiting on ${board.house.waitingOn
                    .map((who) => (who === child.name ? 'you' : who))
                    .join(' and ')}.`}
            </span>
          </span>
        </div>
      )}

      {board.quietToday && (
        <div className="notice" style={{ marginTop: 12 }}>🏅 {board.quietToday} today — no reminders.</div>
      )}
      {board.snoozedUntil ? (
        <div className="notice row between wrap" style={{ marginTop: 12 }}>
          <span>🔕 Reminders off until tomorrow. Chores still count.</span>
          <button className="btn ghost small" type="button" onClick={() => void snooze(false)}>Turn back on</button>
        </div>
      ) : (
        !board.quietToday && !nothingLeft && (
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
            <button className="btn ghost small" type="button" onClick={() => void snooze(true)}>
              🔕 Snooze reminders until tomorrow
            </button>
          </div>
        )
      )}

      {board.overdue.length > 0 && (
        <>
          <h2>{board.words.late}</h2>
          {board.overdue.map((occ) => <QuestRow key={rowKey(occ)} occ={occ} today={board.date} words={board.words} popping={popping === rowKey(occ)} onTap={tap} />)}
        </>
      )}

      <h2>{board.words.today}</h2>
      {board.dueToday.length > 0 ? (
        board.dueToday.map((occ) => <QuestRow key={rowKey(occ)} occ={occ} today={board.date} words={board.words} popping={popping === rowKey(occ)} onTap={tap} />)
      ) : (
        <div className="q-goal" style={{ marginTop: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 34 }}>{nothingLeft ? '🎉' : '👍'}</div>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{nothingLeft ? 'All done for today!' : 'Nothing due today.'}</div>
          {ahead[0] && (
            <div className="q-goal-hint" style={{ marginTop: 4 }}>
              Next up: {ahead[0].title}, {relativeDue(ahead[0].dueDate, board.date)}.
            </div>
          )}
        </div>
      )}

      {board.turns.length > 0 && (
        <>
          <h2>Taking turns</h2>
          {board.turns.map((job) => (
            <TurnRow key={job.id} job={job} childId={board.child.id} onTake={() => void takeTurn(job)} />
          ))}
        </>
      )}

      {ahead.length > 0 && (
        <>
          <h2>{board.words.soon}</h2>
          {ahead.map((occ) => <QuestRow key={rowKey(occ)} occ={occ} today={board.date} words={board.words} popping={popping === rowKey(occ)} onTap={tap} />)}
        </>
      )}

      {board.doneToday.length > 0 && (
        <>
          <h2>{board.words.done}</h2>
          {board.doneToday.map((occ) => <QuestRow key={rowKey(occ)} occ={occ} today={board.date} words={board.words} popping={false} onTap={tap} />)}
        </>
      )}

      {board.extras.length > 0 && (
        <>
          <h2>{board.words.extras}</h2>
          {board.extras.map((extra) => (
            <button key={extra.id} type="button" className="q-row q-extra" onClick={() => setClaiming(extra)}>
              <span className="q-icon">{extra.icon}</span>
              <span className="q-body">
                <span className="q-title">{extra.title}</span>
                <span className="q-meta">
                  <span className="q-coins">
                    {money(extra.amount, board.payday.currency)}{extra.pricing === 'hourly' ? ' an hour' : ' each time'}
                  </span>
                </span>
              </span>
              <span className="q-tick">+</span>
            </button>
          ))}
        </>
      )}

      {board.recentClaims.length > 0 && (
        <>
          <h2>Extras you logged</h2>
          <div className="q-goal" style={{ marginTop: 0, padding: '4px 16px' }}>
            {board.recentClaims.map((claim) => (
              <div key={claim.id} className="q-claim">
                <span>{claim.icon}</span>
                <span className="grow">
                  <span style={{ fontWeight: 700 }}>{claim.title}</span>
                  {claim.minutes ? <span className="q-claim-note"> · {formatMinutes(claim.minutes)}</span> : null}
                  <div className="q-claim-note">{claim.note}</div>
                  {claim.status === 'rejected' && claim.decision_note && (
                    <div className="q-claim-note" style={{ color: 'var(--bad)' }}>{claim.decision_note}</div>
                  )}
                </span>
                <span style={{ textAlign: 'right', flex: 'none' }}>
                  <div style={{ fontWeight: 800, color: claim.status === 'rejected' ? 'var(--muted)' : 'var(--coin)' }}>
                    {claim.status === 'rejected' ? '—' : money(claim.amount, board.payday.currency)}
                  </div>
                  <span className={`badge ${claim.status === 'approved' ? 'good' : claim.status === 'rejected' ? 'bad' : 'warn'}`}>
                    {claim.status === 'approved'
                      ? claim.paid_at
                        ? 'Paid ✓'
                        : 'OK! · owed'
                      : claim.status === 'rejected'
                        ? 'No'
                        : 'Waiting'}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {claiming && (
        <ClaimSheet extra={claiming} currency={board.payday.currency} onClose={() => setClaiming(null)} onClaimed={claimed} />
      )}
      {toast && <div className="q-toast">{toast}</div>}

      {celebration && (
        <Celebration
          kind={celebration.kind}
          detail={celebration.detail}
          name={celebration.kind === 'house' ? board.house?.reward ?? 'a treat' : child.name}
          streak={streak.days}
          amount={payday.amount}
          currency={payday.currency}
          paydayLabel={paydayWord(board)}
          onClose={closeCelebration}
        />
      )}
    </>
  );
}

function rowKey(occ: Occurrence): string {
  return `${occ.choreId}:${occ.periodKey}`;
}

function QuestRow({
  occ,
  today,
  words,
  popping,
  onTap,
}: {
  occ: Occurrence;
  today: string;
  words: QuestWords;
  popping: boolean;
  onTap: (occ: Occurrence) => void;
}): JSX.Element {
  const done = occ.state === 'done';
  const late = occ.state === 'overdue';
  const cls = `q-row${done ? ' done' : ''}${late ? ' late' : ''}${popping ? ' popping' : ''}`;
  const when = occ.frequency === 'daily' ? null : relativeDue(occ.dueDate, today);
  // Daily chores are the background hum; the rarer ones get named so a weekly
  // job doesn't look like just another Tuesday.
  const cadence = occ.frequency === 'daily' ? null : words[occ.frequency];
  return (
    <button type="button" className={cls} onClick={() => onTap(occ)}>
      <span className="q-icon">{occ.icon}</span>
      <span className="q-body">
        <span className="q-title">{occ.title}</span>
        <span className="q-meta">
          <span className="q-coins">{coins(occ.points)}</span>
          {cadence && <span className="q-cadence">{cadence}</span>}
          {late && <span className="q-late">{occ.alsoMissed ? `missed ${occ.alsoMissed + 1}×` : 'late'}</span>}
          {!late && !done && when && <span>due {when}</span>}
          {!occ.countsForPay && <span>bonus</span>}
        </span>
      </span>
      <span className="q-tick">{TICK}</span>
      {popping && <Burst points={occ.points} />}
    </button>
  );
}

/**
 * One shared job. Whoever has taken fewest turns is up next, but the button is
 * live for both: doing one out of turn is how you get ahead, which is exactly
 * how the list on the fridge worked.
 */
function TurnRow({
  job,
  childId,
  onTake,
}: {
  job: TurnJob;
  childId: number;
  onTake: () => void;
}): JSX.Element {
  const mine = job.nextChildId === childId;
  const me = job.standings.find((s) => s.childId === childId);
  const other = job.standings.filter((s) => s.childId !== childId);
  const lead = me && me.ahead > 0 ? `You're ${me.ahead} ahead` : null;
  const owed = !mine ? null : other.find((s) => s.ahead > 0);
  return (
    <div className={`q-row q-turn${mine ? ' mine' : ''}`}>
      <span className="q-icon">{job.icon}</span>
      <span className="q-body">
        <span className="q-title">{job.title}</span>
        <span className="q-meta">
          <span className="q-coins">{coins(job.points)}</span>
          <span className={mine ? 'q-turn-you' : ''}>{mine ? 'Your turn' : `${job.nextName}'s turn`}</span>
          {lead && <span>{lead}</span>}
          {owed && <span>{owed.name} is {owed.ahead} ahead</span>}
        </span>
      </span>
      <button type="button" className="btn small" onClick={onTake}>
        I did it
      </button>
    </div>
  );
}

/** Coins that fly out of the tick, plus a floating "+N". */
function Burst({ points }: { points: number }): JSX.Element {
  const particles = Array.from({ length: 7 }, (_, i) => ({
    id: i,
    dx: `${Math.round(-70 + Math.random() * 90)}px`,
    dy: `${Math.round(-80 - Math.random() * 40)}px`,
    rot: `${Math.round(Math.random() * 360 - 180)}deg`,
    delay: `${i * 0.03}s`,
  }));
  return (
    <>
      <span className="q-float">+{points} 🪙</span>
      {particles.map((p) => (
        <span
          key={p.id}
          className="q-particle"
          style={{
            ['--dx' as string]: p.dx,
            ['--dy' as string]: p.dy,
            ['--rot' as string]: p.rot,
            animationDelay: p.delay,
          }}
        >
          🪙
        </span>
      ))}
    </>
  );
}
